import React from 'react'

import { TableGraphSelection } from '../../../types/gotypes'
import { PlatinumData } from '../../../types/carbon/models/PlatinumData'
import {
  extractRowLabels,
  extractColumnLabels,
  extractRowTypes,
  extractColumnTypes,
} from '../../platinum-graphs/utils/convertPlatinumData'
import {
  buildPositionalSelection,
  chartableAxis,
  effectiveSelectionPositions,
  fromDisplayPositions,
  toDisplayPositions,
} from '../../../utils/tableSelection'
import { SelectDataRow } from './SelectDataRow'

interface TableSelectionEditorProps {
  data: PlatinumData
  selection: TableGraphSelection | undefined
  onChange: (selection: TableGraphSelection | undefined) => void
  /** One-column chart types (#1172): Columns renders as a single selector. */
  singleColumn?: boolean
}

/**
 * The chart widget's "Select Data" controls (#968 Obs 10): one compact row —
 * Rows dropdown · Columns dropdown · Switch Axis — wrapping the shared
 * SelectDataRow.
 *
 * Selection is POSITIONAL (Obs 11, confirmed decision, consistent with
 * Carbon): a selected slot always renders whatever occupies that position,
 * whatever its label; "all" carries no filter and refreshes with the table.
 * Legacy name/rolling selections still display (mapped onto the positions
 * they occupy today), but the first edit through this control rewrites the
 * widget positionally — which also removes the old duplicate-label collision
 * where unchecking one brand's "Cases where you fill it" row unchecked every
 * brand's.
 *
 * Both dropdowns offer only CHARTABLE vecs (#968 comment 3): a chart never
 * draws base rows, so listing them gives options that do nothing. The
 * dropdowns therefore address OPTION indices while the selection stores TRUE
 * vec positions, and the two are mapped across this boundary — filtering the
 * labels alone would shift every index and re-point the selection. Base
 * positions an existing widget already stored are carried through untouched.
 */
export const TableSelectionEditor: React.FC<TableSelectionEditorProps> = ({
  data,
  selection,
  onChange,
  singleColumn = false,
}) => {
  const rowAxis = chartableAxis(extractRowLabels(data), extractRowTypes(data))
  const columnAxis = chartableAxis(extractColumnLabels(data), extractColumnTypes(data))
  const { rowPositions, columnPositions } = effectiveSelectionPositions(selection, data)
  const switchAxis = selection?.switch_axis || false

  // Stored positions with no option in the dropdown — bases. Kept so editing a
  // data row does not quietly drop them from the widget's selection.
  const storedBaseRows = rowPositions.filter(p => !rowAxis.positions.includes(p))
  const storedBaseColumns = columnPositions.filter(p => !columnAxis.positions.includes(p))

  const emit = (rows: number[], columns: number[], nextSwitchAxis: boolean) => {
    onChange(buildPositionalSelection(rows, columns, nextSwitchAxis, data))
  }

  return (
    <SelectDataRow
      rowOptions={rowAxis.options}
      columnOptions={columnAxis.options}
      rowPositions={toDisplayPositions(rowPositions, rowAxis.positions)}
      columnPositions={toDisplayPositions(columnPositions, columnAxis.positions)}
      switchAxis={switchAxis}
      onRowPositionsChange={(display) =>
        emit(
          fromDisplayPositions(display, rowAxis.positions, storedBaseRows),
          columnPositions,
          switchAxis,
        )
      }
      onColumnPositionsChange={(display) =>
        emit(
          rowPositions,
          // Single-column mode (#1172) drops stored base columns instead of
          // carrying them: the generator plots the FIRST header of the slice,
          // so a carried base sitting before the picked column would be what
          // actually renders.
          fromDisplayPositions(display, columnAxis.positions, singleColumn ? [] : storedBaseColumns),
          switchAxis,
        )
      }
      onSwitchAxisChange={(next) => emit(rowPositions, columnPositions, next)}
      singleColumn={singleColumn}
    />
  )
}

export default TableSelectionEditor
